const SSO_ERROR_MESSAGES: Record<string, string> = {
  identity_not_found: "User is not signed up yet.",
  email_not_verified:
    "Your email address has not been verified. Please verify your email and try again.",
  invalid_state: "Your sign-in session expired. Please try again.",
  unsupported_provider: "This sign-in provider is not supported.",
  redirect_uri_not_allowed:
    "Sign-in configuration error. Please contact support.",
  missing_identity_claims:
    "We could not verify your account information. Please try again.",
  signup_directory_unavailable:
    "Sign-up is temporarily unavailable. Please try again later.",
  invalid_signup_id: "Your sign-up session expired. Please start again.",
  unsupported_intent: "Invalid sign-in request. Please try again.",
  signup_id_required: "Your sign-up session expired. Please start again.",
  oidc_issuer_invalid: "Sign-in configuration error. Please contact support.",
  oidc_nonce_mismatch: "Your sign-in session expired. Please try again.",
  oidc_token_exchange_rejected: "Sign-in was rejected. Please try again.",
  oidc_missing_id_token: "We could not complete sign-in. Please try again.",
  oidc_no_matching_jwk: "Sign-in configuration error. Please contact support.",
  code_or_identity_required: "We could not complete sign-in. Please try again.",
};

const SSO_ERROR_PREFIX_MESSAGES: Array<{ prefix: string; message: string }> = [
  {
    prefix: "signup_provision_failed",
    message:
      "We could not complete your sign-up. Please try again or contact support.",
  },
  {
    prefix: "oidc_token_exchange_failed",
    message: "We could not complete sign-in. Please try again.",
  },
  {
    prefix: "oidc_jwks_fetch_failed",
    message:
      "Sign-in service is temporarily unavailable. Please try again later.",
  },
  {
    prefix: "oidc_id_token_invalid",
    message: "We could not verify your sign-in. Please try again.",
  },
];

function extractAuthErrorDetail(raw: string): string {
  const trimmed = raw.trim();
  if (!trimmed) {
    return "";
  }
  if (trimmed.startsWith("{")) {
    try {
      const parsed = JSON.parse(trimmed) as {
        detail?: unknown;
        message?: unknown;
      };
      const detail = parsed.detail ?? parsed.message;
      if (detail != null && String(detail).trim()) {
        return String(detail).trim();
      }
    } catch {
      /* fall through */
    }
  }
  return trimmed;
}

/** Map identity OIDC / SSO error codes to user-facing copy. */
export function formatSsoErrorMessage(detail: string, fallback: string): string {
  const normalized = detail.trim();
  if (!normalized) {
    return fallback;
  }
  const exact = SSO_ERROR_MESSAGES[normalized];
  if (exact) {
    return exact;
  }
  const lower = normalized.toLowerCase();
  const exactLower = SSO_ERROR_MESSAGES[lower];
  if (exactLower) {
    return exactLower;
  }
  for (const { prefix, message } of SSO_ERROR_PREFIX_MESSAGES) {
    if (lower === prefix || lower.startsWith(`${prefix}:`)) {
      return message;
    }
  }
  return normalized;
}

/** Parse auth service errors and return user-friendly SSO messages when applicable. */
export function parseAuthErrorMessage(err: unknown, fallback: string): string {
  const message = err instanceof Error ? err.message : fallback;
  const detail = extractAuthErrorDetail(message);
  if (!detail) {
    return fallback;
  }
  return formatSsoErrorMessage(detail, fallback);
}
